<script module lang="ts">
  // Keep in sync with $topbar-height in #general/scss/_utils.scss
  export const topBarHeight = 46
</script>
<script lang="ts">
  import { config } from '#app/config'
  import { screen } from '#app/lib/components/stores/screen'
  import { locationStore } from '#app/lib/location_store'
  import { loadFragmentLink, loadInternalLink } from '#app/lib/utils'
  import type { ClickEvent } from '#app/types/declarations/svelte'
  import BarcodeScannerSvg from '#assets/images/barcode_scanner_svg.svelte'
  import TopBarButtons from '#components/top_bar_buttons.svelte'
  import TopBarLanguagePicker from '#components/top_bar_language_picker.svelte'
  import GlobalSearchBar from '#search/components/global_search_bar.svelte'
  import { I18n, i18n } from '#user/lib/i18n'
  import { mainUser } from '#user/lib/main_user'

  let {
    showBarcodeScanner = $bindable(),
  }: {
    showBarcodeScanner: boolean
  } = $props()

  const { instanceName } = config
  const shortName = instanceName.slice(0, 3)
  const smallScreen = $derived($screen.isSmallerThan('$small-screen'))
  const onConnectionPage = $derived(($locationStore.section === 'signup') || ($locationStore.section === 'login'))
  const displayConnectionButtons = $derived(!(smallScreen && onConnectionPage))

  function onBarcodeScannerButtonClick (e: ClickEvent) {
    e.stopPropagation()
    showBarcodeScanner = true
  }
</script>

<nav>
  <h1 class="respect-case">
    <a id="home" href="/" onclick={loadInternalLink}>{smallScreen ? shortName : instanceName}</a>
  </h1>

  <a
    id="goToMain"
    href="#main"
    onclick={loadFragmentLink}
    title={I18n('skip to main content')}
    aria-label={I18n('skip to main content')}
  ></a>

  <div class="extended-search-group">
    <GlobalSearchBar />
    <div class="vertical-separator"></div>
    <button
      class="barcode-scanner-button"
      title={i18n('Search a book by scanning its barcode')}
      onclick={onBarcodeScannerButtonClick}
    >
      <BarcodeScannerSvg />
    </button>
  </div>

  <TopBarLanguagePicker />

  {#if mainUser}
    <TopBarButtons />
  {:else if displayConnectionButtons}
    <a
      class="signup-request"
      href="/signup"
      onclick={loadInternalLink}
    >
      {I18n('sign up')}
    </a>
    <a
      class="login-request"
      href="/login"
      onclick={loadInternalLink}
    >
      {I18n('login')}
    </a>
  {/if}
</nav>

<style lang="scss">
  @import "general/scss/utils";

  nav{
    @include display-flex(row, center, flex-start);
    background-color: $topbar-bg-color;
    // Screens that have enough vertical space to display the full top bar menu,
    // without needing to scroll
    @media screen and (height >= $top-bar-fixed-threshold){
      // Use position:fixed to make the top-bar stick to the screen top
      @include position(fixed, 0, 0, null, 0);
    }
  }
  h1{
    font-size: 2rem;
    font-weight: bold;
    line-height: 2rem;
    // /!\ Defines the top-bar height
    padding: 0.1rem 0.5rem;
  }
  a#home{
    color: white;
    font-weight: bold;
  }
  .signup-request, .login-request{
    font-weight: bold;
    color: white;
    white-space: nowrap;
    @include display-flex(row, center, center);
    align-self: stretch;
    padding: 0 1em;
  }
  .signup-request{
    @include bg-hover($secondary-color, 5%);
    color: $dark-grey;
  }
  .login-request{
    @include bg-hover($success-color, 5%);
    color: white;
  }

  .extended-search-group{
    flex: 1 0 0;
    @include display-flex(row, center, center);
    margin: 0 auto;
    margin-block-end: 0;
    background-color: white;
    // 7 = max number of search section on a row
    max-width: calc(7 * $search-section-width + 3.1rem);
    height: 2.3rem;
    @include radius;
  }
  .vertical-separator{
    border-inline-start: 1px solid #ccc;
    height: 1.5rem;
  }
  .barcode-scanner-button{
    height: 100%;
    padding: 0.5rem;
    @include display-flex(row, center, center);
    :global(svg){
      fill: $grey;
      width: 2rem;
      height: 2rem;
    }
  }

  /* Large screens */
  @media screen and (width >= $small-screen){
    .signup-request{
      // Push away the #language-picker
      margin-inline-start: 1em;
    }
  }

  /* Small screens */
  @media screen and (width < $small-screen){
    nav{
      padding: 0.2em 0;
    }
    h1{
      flex: 0 0 auto;
      font-size: 1.1rem;
      background-color: $light-blue;
      @include radius;
      width: 1.8em;
      height: 1.8em;
      padding: 0;
      text-align: center;
      margin-inline-start: 0.2em;
      margin-inline-end: 0.4em;
    }
    // Showing signup and login button at the bottom of the screen
    .signup-request, .login-request{
      position: fixed;
      inset-block-end: 0;
      height: $smallscreen-connection-buttons-height;
    }
    .signup-request{
      inset-inline-start: 0;
      inset-inline-end: 50vw;
    }
    .login-request{
      inset-inline-start: 50vw;
      inset-inline-end: 0;
    }
  }
</style>
